// NOTICE: KAPTION-INTEGRATION
// Embedded channel helpers for the dashboard (embedded-channel.DASHBOARD.1-5).
// An Embedded inbox is a Channel::WebWidget inbox whose payload carries
// `embedded` ({ channel_id, identity_mode, legacy_identifier_hash,
// continuity_mode }). Its public Channel ID is the
// widget website_token on every platform.
import { INBOX_TYPES } from 'dashboard/helper/inbox';

export const EMBEDDED_FLAG = 'KAPTION_EMBEDDED_CHANNEL';

export const EMBEDDED_PLATFORMS = Object.freeze({
  WEB: 'web',
  IOS: 'ios',
  ANDROID: 'android',
});

export const EMBEDDED_TAB_KEYS = Object.freeze({
  WEB: 'embedded_web',
  IOS: 'embedded_ios',
  ANDROID: 'embedded_android',
  IDENTITY: 'embedded_identity',
  STUDIO: 'embedded_studio',
});

export const isEmbeddedInbox = inbox =>
  !!inbox && inbox.channel_type === INBOX_TYPES.WEB && !!inbox.embedded;

export const isPlainWebWidgetInbox = inbox =>
  !!inbox && inbox.channel_type === INBOX_TYPES.WEB && !inbox.embedded;

export const embeddedChannelId = inbox =>
  inbox?.embedded?.channel_id || inbox?.website_token || '';

// embedded-channel.API.7: conversations without a platform are web.
export const conversationPlatform = conversation => {
  const platform = conversation?.additional_attributes?.platform;
  return Object.values(EMBEDDED_PLATFORMS).includes(platform)
    ? platform
    : EMBEDDED_PLATFORMS.WEB;
};

// embedded-channel.DASHBOARD.3: the Embedded tabs, inserted right after the
// upstream "Settings" tab. The upstream widget tabs (pre-chat form, business
// hours, CSAT, widget builder, configuration...) stay as they are.
export const embeddedTabs = t => [
  { key: EMBEDDED_TAB_KEYS.WEB, name: t('INBOX_MGMT.EMBEDDED.TABS.WEB') },
  { key: EMBEDDED_TAB_KEYS.IOS, name: t('INBOX_MGMT.EMBEDDED.TABS.IOS') },
  {
    key: EMBEDDED_TAB_KEYS.ANDROID,
    name: t('INBOX_MGMT.EMBEDDED.TABS.ANDROID'),
  },
  {
    key: EMBEDDED_TAB_KEYS.IDENTITY,
    name: t('INBOX_MGMT.EMBEDDED.TABS.IDENTITY'),
  },
  { key: EMBEDDED_TAB_KEYS.STUDIO, name: t('INBOX_MGMT.EMBEDDED.TABS.STUDIO') },
];

// Returns the upstream tab list with the Embedded tabs added for an Embedded
// inbox (flag on). The Studio tab replaces the plain widget's Widget Studio tab.
export const withEmbeddedTabs = (tabs, { inbox, flagEnabled, t }) => {
  if (!flagEnabled || !isEmbeddedInbox(inbox)) return tabs;
  const rest = tabs.filter(tab => tab.key !== 'kaptionWidgetStudio');
  const settingsIndex = rest.findIndex(tab => tab.key === 'inbox_settings');
  const at = settingsIndex === -1 ? 0 : settingsIndex + 1;
  return [...rest.slice(0, at), ...embeddedTabs(t), ...rest.slice(at)];
};

export const installationUrl = inbox => {
  const fromScript = /BASE_URL\s*=\s*"([^"]*)"/.exec(
    inbox?.web_widget_script || ''
  )?.[1];
  return (
    fromScript ||
    window.chatwootConfig?.hostURL ||
    window.location.origin
  ).replace(/\/+$/, '');
};

// embedded-channel.WEB.4: window.rumoSettings.channel is read as websiteToken.
export const webSnippet = ({ channelId, baseUrl }) =>
  `<script>
  window.rumoSettings = { channel: '${channelId}' };
  (function(d,t) {
    var BASE_URL="${baseUrl}";
    var g=d.createElement(t),s=d.getElementsByTagName(t)[0];
    g.src=BASE_URL+"/packs/js/sdk.js";
    g.async = true;
    s.parentNode.insertBefore(g,s);
    g.onload=function(){
      window.rumoSDK.run({ channel: '${channelId}', baseUrl: BASE_URL });
    }
  })(document,"script");
</script>`;

export const webSetUserSnippet = () =>
  `window.addEventListener('rumo:ready', function () {
  // identityToken: a short-lived JWT your backend signs with the identity secret
  window.$rumo.setUser('<user id>', { identityToken: '<identity token>' });
});`;

export const iosSnippet = ({ channelId, baseUrl }) =>
  `Rumo.configure(channel: "${channelId}", baseURL: URL(string: "${baseUrl}")!)`;

export const androidSnippet = ({ channelId, baseUrl }) =>
  `Rumo.configure(context, RumoConfig(channel = "${channelId}", baseUrl = "${baseUrl}"))`;

export const reactNativeSnippet = ({ channelId, baseUrl }) =>
  `Rumo.configure('${channelId}', '${baseUrl}')`;

export const CONTINUITY_MODES = Object.freeze([
  'push_fallback_email',
  'push',
  'email',
  'both',
]);
